
import React, { useState } from 'react'
import { Plus, Eye, Edit, Trash2, Calendar, BarChart3, Clock, Users, DollarSign, Target, TrendingUp, Search, Filter, MessageSquare, FileDown, Settings, X, Play, CheckCircle, AlertTriangle, ArrowUp, ArrowDown, Package, UserCheck, Building, Calculator, Download } from 'lucide-react'
import { usePlanejamentoObras } from '../hooks/usePlanejamentoObras'
import MetricsCard from '../components/MetricsCard'
import PlanejamentoObraForm from '../components/PlanejamentoObraForm'
import AtividadeForm from '../components/AtividadeForm'
import NotificationBellObras from '../components/NotificationBellObras'
import GanttChart from '../components/GanttChart'
import { exportPlanejamentoSemanalPDF } from '../utils/pdfExportUtils'
import toast from 'react-hot-toast'

const PlanejamentoObras: React.FC = () => {
  const {
    obras,
    atividades,
    mensagens,
    solicitacoes,
    loading,
    metrics,
    createObra,
    updateObra,
    deleteObra,
    createAtividade,
    updateAtividade,
    deleteAtividade,
    sendMensagem,
    createSolicitacao,
    getContratosAssinados,
    getOrcamentosFechados,
    getFuncionariosDisponiveis,
    getInsumosDisponiveis,
    getAtividadesByObra,
    getMensagensByObra,
    debitarEstoque,
    atualizarHistoricoFuncionario,
    sincronizarCustosAdministrativos
  } = usePlanejamentoObras()

  // Estados do componente
  const [activeTab, setActiveTab] = useState<'obras' | 'planejamento' | 'semanal'>('obras')
  const [selectedObra, setSelectedObra] = useState<any>(null)
  const [showObraForm, setShowObraForm] = useState(false)
  const [showAtividadeForm, setShowAtividadeForm] = useState(false)
  const [showGantt, setShowGantt] = useState(false)
  const [editingObra, setEditingObra] = useState<any>(null)
  const [editingAtividade, setEditingAtividade] = useState<any>(null)
  const [contratosAssinados, setContratosAssinados] = useState<any[]>([])
  const [orcamentosFechados, setOrcamentosFechados] = useState<any[]>([])
  const [funcionarios, setFuncionarios] = useState<any[]>([])
  const [insumos, setInsumos] = useState<any[]>([])

  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [funcionarioFilter, setFuncionarioFilter] = useState<string>('all')
  const [obraFilter, setObraFilter] = useState<string>('all')

  // Estados de mensagens
  const [showChat, setShowChat] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [selectedFuncionario, setSelectedFuncionario] = useState<any>(null)
  const [selectedAtividade, setSelectedAtividade] = useState<any>(null)

  // Estados para modais de gestão
  const [showFuncionariosModal, setShowFuncionariosModal] = useState(false)
  const [showMaoObraModal, setShowMaoObraModal] = useState(false)
  
  // 🔥 CORREÇÃO: Estados para formulários de etapa e subetapa
  const [showEtapaForm, setShowEtapaForm] = useState(false)
  const [showSubetapaForm, setShowSubetapaForm] = useState(false)
  const [selectedEtapaIndex, setSelectedEtapaIndex] = useState<number>(-1)
  const [newEtapa, setNewEtapa] = useState({
    nome: '',
    descricao: '',
    dataInicioPrevisto: '',
    dataTerminoPrevisto: '',
    valorOrcado: 0
  })
  const [newSubetapa, setNewSubetapa] = useState({
    nome: '',
    descricao: '',
    dataInicioPrevisto: '',
    dataTerminoPrevisto: '',
    valorOrcado: 0
  })

  React.useEffect(() => {
    loadInitialData()
  }, [])

  const loadInitialData = async () => {
    try {
      const [contratosData, orcamentosData, funcionariosData, insumosData] = await Promise.all([
        getContratosAssinados(),
        getOrcamentosFechados(),
        getFuncionariosDisponiveis(),
        getInsumosDisponiveis()
      ])
      
      setContratosAssinados(contratosData)
      setOrcamentosFechados(orcamentosData)
      setFuncionarios(funcionariosData)
      setInsumos(insumosData)

      console.log(`Carregados ${orcamentosData.length} orçamentos fechados para planejamento de obra`)
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error)
    }
  }

  // 🔥 CORREÇÃO: Função para adicionar etapa diretamente
  const handleAddEtapa = async () => {
    if (!selectedObra || !newEtapa.nome.trim()) return

    try {
      const etapasAtuais = selectedObra.etapas || []
      const novaEtapa = {
        id: `etapa_${Date.now()}`,
        numero: `${etapasAtuais.length + 1}`,
        nome: newEtapa.nome,
        descricao: newEtapa.descricao,
        ordem: etapasAtuais.length + 1,
        status: 'previsto',
        dataInicioPrevisto: newEtapa.dataInicioPrevisto || '',
        dataTerminoPrevisto: newEtapa.dataTerminoPrevisto || '',
        dataInicioReal: '',
        dataTerminoReal: '',
        valorOrcado: newEtapa.valorOrcado || 0,
        valorRealizado: 0,
        percentualConcluido: 0,
        dependencias: [],
        subetapas: [],
        insumos: [],
        maoObra: []
      }

      const etapasAtualizadas = [...etapasAtuais, novaEtapa]
      
      await updateObra(selectedObra._id, {
        etapas: etapasAtualizadas
      })

      // Atualizar obra local
      setSelectedObra(prev => ({
        ...prev,
        etapas: etapasAtualizadas
      }))

      // Limpar formulário e fechar modal
      setNewEtapa({
        nome: '',
        descricao: '',
        dataInicioPrevisto: '',
        dataTerminoPrevisto: '',
        valorOrcado: 0
      })
      setShowEtapaForm(false)
    } catch (error) {
      console.error('Erro ao adicionar etapa:', error)
    }
  }

  // 🔥 CORREÇÃO: Função para adicionar subetapa
  const handleAddSubetapa = async () => {
    if (!selectedObra || selectedEtapaIndex === -1 || !newSubetapa.nome.trim()) return

    try {
      const etapasAtuais = [...selectedObra.etapas]
      const etapa = etapasAtuais[selectedEtapaIndex]
      const subetapasAtuais = etapa.subetapas || []
      
      const novaSubetapa = {
        id: `subetapa_${Date.now()}`,
        numero: `${etapa.numero}.${subetapasAtuais.length + 1}`,
        nome: newSubetapa.nome,
        descricao: newSubetapa.descricao,
        status: 'previsto',
        dataInicioPrevisto: newSubetapa.dataInicioPrevisto || '',
        dataTerminoPrevisto: newSubetapa.dataTerminoPrevisto || '',
        dataInicioReal: '',
        dataTerminoReal: '',
        valorOrcado: newSubetapa.valorOrcado || 0,
        valorRealizado: 0,
        percentualConcluido: 0,
        insumos: [],
        maoObra: []
      }

      etapa.subetapas = [...subetapasAtuais, novaSubetapa]
      
      await updateObra(selectedObra._id, {
        etapas: etapasAtuais
      })

      // Atualizar obra local
      setSelectedObra(prev => ({
        ...prev,
        etapas: etapasAtuais
      }))

      // Limpar formulário e fechar modal
      setNewSubetapa({
        nome: '',
        descricao: '',
        dataInicioPrevisto: '',
        dataTerminoPrevisto: '',
        valorOrcado: 0
      })
      setShowSubetapaForm(false)
      setSelectedEtapaIndex(-1)
    } catch (error) {
      console.error('Erro ao adicionar subetapa:', error)
    }
  }

  // Filtrar obras
  const filteredObras = obras.filter(obra => {
    const matchesSearch = obra.nomeObra.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || obra.status === statusFilter
    return matchesSearch && matchesStatus
  })

  // Filtrar atividades
  const filteredAtividades = atividades.filter(atividade => {
    const matchesSearch = atividade.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         atividade.funcionarioResponsavel.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || atividade.status === statusFilter
    const matchesFuncionario = funcionarioFilter === 'all' || atividade.funcionarioId === funcionarioFilter
    const matchesObra = obraFilter === 'all' || atividade.obraId === obraFilter
    return matchesSearch && matchesStatus && matchesFuncionario && matchesObra
  })

  // Calculate percentage change
  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous) * 100
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Calculate days difference
  const calculateDaysDifference = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = end.getTime() - start.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planejamento': return 'bg-gray-100 text-gray-800'
      case 'em_andamento': return 'bg-blue-100 text-blue-800'
      case 'pausada': return 'bg-yellow-100 text-yellow-800'
      case 'concluida': return 'bg-green-100 text-green-800'
      case 'cancelada': return 'bg-red-100 text-red-800'
      case 'pendente': return 'bg-gray-100 text-gray-800'
      case 'atrasada': return 'bg-red-100 text-red-800'
      case 'realizado': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'baixa': return 'bg-green-100 text-green-800'
      case 'media': return 'bg-yellow-100 text-yellow-800'
      case 'alta': return 'bg-orange-100 text-orange-800'
      case 'urgente': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Handle create obra from contract
  const handleCreateFromContract = async (contratoId: string) => {
    const contrato = contratosAssinados.find(c => c._id === contratoId)
    if (contrato) {
      setEditingObra({
        contratoId,
        numeroContrato: contrato.numero,
        nomeObra: contrato.nomeProjeto,
        descricaoProjeto: contrato.descricaoProjeto || '',
        valorOrcado: contrato.valorTotal,
        bdi: contrato.bdi || 0,
        etapas: contrato.etapas || []
      })
      setShowObraForm(true)
    }
  }

  // Handle create obra from orçamento fechado
  const handleCreateFromOrcamento = async (orcamentoId: string) => {
    const orcamento = orcamentosFechados.find(o => o._id === orcamentoId)
    if (orcamento) {
      setEditingObra({
        orcamentoId,
        nomeObra: orcamento.nomeProjeto,
        descricaoProjeto: orcamento.descricao || '',
        valorOrcado: orcamento.valorTotal,
        bdi: orcamento.bdi || 0,
        etapas: orcamento.etapas || []
      })
      setShowObraForm(true)
    }
  }

  // Handle view obra details
  const handleViewObra = (obra: any) => {
    setSelectedObra(obra)
    setActiveTab('planejamento')
  }

  // Handle iniciar obra
  const handleIniciarObra = async (obra: any) => {
    const now = new Date().toISOString()
    const dataTerminoPrevisto = new Date()
    dataTerminoPrevisto.setMonth(dataTerminoPrevisto.getMonth() + 6) // 6 meses de previsão

    await updateObra(obra._id, {
      status: 'em_andamento',
      dataInicioRealReal: now,
      dataTerminoPrevisto: dataTerminoPrevisto.toISOString()
    })
  }

  // Handle concluir obra
  const handleConcluirObra = async (obra: any) => {
    const now = new Date().toISOString()
    
    await updateObra(obra._id, {
      status: 'concluida',
      dataTerminoReal: now,
      progressoGeral: 100
    })
  }

  // Handle send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedFuncionario) return

    try {
      await sendMensagem({
        obraId: selectedObra?._id || '',
        atividadeId: selectedAtividade?._id,
        remetente: 'Supervisor',
        remetenteId: 'supervisor_001',
        destinatario: selectedFuncionario.nome,
        destinatarioId: selectedFuncionario._id,
        mensagem: newMessage,
        tipo: 'mensagem'
      })
      setNewMessage('')
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error)
    }
  }

  // Handle delete
  const handleDelete = async (id: string, type: 'obra' | 'atividade') => {
    if (confirm(`Tem certeza que deseja excluir esta ${type}?`)) {
      if (type === 'obra') {
        await deleteObra(id)
      } else {
        await deleteAtividade(id)
      }
    }
  }

  // Calculate lucro líquido for obra
  const calculateLucroLiquido = (obra: any) => {
    return obra.valorOrcado - obra.valorRealizado - obra.custosAdministrativos
  }

  // Get current etapa
  const getCurrentEtapa = (obra: any) => {
    if (!obra.etapas || obra.etapas.length === 0) return 'N/A'
    
    const etapaAtual = obra.etapas.find((etapa: any) => 
      etapa.status === 'em_andamento' || etapa.percentualConcluido < 100
    )
    
    return etapaAtual ? `${etapaAtual.numero} - ${etapaAtual.nome}` : 'Todas concluídas'
  }

  // Get funcionários envolvidos na obra
  const getFuncionariosEnvolvidos = (obra: any) => {
    const funcionariosIds = new Set()
    
    // Funcionários das atividades
    atividades
      .filter(atividade => atividade.obraId === obra._id)
      .forEach(atividade => funcionariosIds.add(atividade.funcionarioId))
    
    // Funcionários das etapas
    obra.etapas?.forEach((etapa: any) => {
      etapa.maoObra?.forEach((mo: any) => funcionariosIds.add(mo.funcionarioId))
      etapa.subetapas?.forEach((subetapa: any) => {
        subetapa.maoObra?.forEach((mo: any) => funcionariosIds.add(mo.funcionarioId))
      })
    })
    
    return funcionarios.filter(func => funcionariosIds.has(func._id))
  }

  // Get mão de obra envolvida na obra
  const getMaoObraEnvolvida = (obra: any) => {
    const maoObra: any[] = []
    
    obra.etapas?.forEach((etapa: any) => {
      etapa.maoObra?.forEach((mo: any) => {
        maoObra.push({ ...mo, etapa: etapa.nome, tipo: 'Etapa' })
      })
      etapa.subetapas?.forEach((subetapa: any) => {
        subetapa.maoObra?.forEach((mo: any) => {
          maoObra.push({ ...mo, etapa: `${etapa.nome} > ${subetapa.nome}`, tipo: 'Subetapa' })
        })
      })
    })
    
    return maoObra
  }

  // 🔥 NOVA FUNCIONALIDADE: Export planejamento semanal to PDF
  const handleExportPlanejamentoSemanalPDF = async () => {
    if (!selectedObra) {
      toast.error('Nenhuma obra selecionada')
      return
    }
    
    try {
      toast.loading('Gerando PDF...', { id: 'pdf-generation' })
      
      const atividadesObra = filteredAtividades.filter(a => a.obraId === selectedObra._id)
      
      await exportPlanejamentoSemanalPDF({
        obra: selectedObra,
        atividades: atividadesObra,
        funcionarios
      })
      
      toast.success('PDF gerado com sucesso!', { id: 'pdf-generation' })
    } catch (error) {
      console.error('Erro ao gerar PDF:', error)
      toast.error('Erro ao gerar PDF. Tente novamente.', { id: 'pdf-generation' })
    }
  }

  // Check if orcamento has obra created
  const hasObraCreated = (orcamentoId: string) => {
    return obras.some(obra => obra.orcamentoId === orcamentoId)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando planejamento...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Planejamento de Obra</h1>
              <p className="text-gray-600 mt-1">Gestão completa do planejamento e execução de obras</p>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBellObras obras={obras} solicitacoes={solicitacoes} />
              <button
                onClick={() => setShowObraForm(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
              >
                <Plus size={20} />
                Nova Obra
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 🔥 DASHBOARD PRINCIPAL - Métricas Expandidas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricsCard
            title="Total de Obras"
            value={metrics.totalObras}
            icon={BarChart3}
            color="#3B82F6"
            description={`${calculatePercentageChange(metrics.totalObras, metrics.totalObrasAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.totalObras, metrics.totalObrasAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Etapas Concluídas"
            value={metrics.etapasConcluidas}
            icon={CheckCircle}
            color="#10B981"
            description={`${calculatePercentageChange(metrics.etapasConcluidas, metrics.etapasConcluidasAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.etapasConcluidas, metrics.etapasConcluidasAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Valor Realizado"
            value={formatCurrency(metrics.valorTotalRealizado)}
            icon={DollarSign}
            color="#059669"
            description={`${calculatePercentageChange(metrics.valorTotalRealizado, metrics.valorTotalRealizadoAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.valorTotalRealizado, metrics.valorTotalRealizadoAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Custos Administrativos"
            value={formatCurrency(metrics.custosAdministrativosTotal)}
            icon={Calculator}
            color="#F59E0B"
            description={`${calculatePercentageChange(metrics.custosAdministrativosTotal, metrics.custosAdministrativosTotalAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.custosAdministrativosTotal, metrics.custosAdministrativosTotalAnterior).toFixed(1)}% vs mês anterior`}
          />
        </div>

        {/* Segunda linha de métricas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricsCard
            title="Progresso Médio"
            value={`${metrics.progressoMedio.toFixed(1)}%`}
            icon={Target}
            color="#8B5CF6"
            description={`${calculatePercentageChange(metrics.progressoMedio, metrics.progressoMedioAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.progressoMedio, metrics.progressoMedioAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Solicitações Pendentes"
            value={metrics.solicitacoesPendentes}
            icon={Package}
            color="#EF4444"
            description={`${calculatePercentageChange(metrics.solicitacoesPendentes, metrics.solicitacoesPendentesAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.solicitacoesPendentes, metrics.solicitacoesPendentesAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Obras em Andamento"
            value={metrics.obrasAndamento}
            icon={Clock}
            color="#06B6D4"
            description={`${calculatePercentageChange(metrics.obrasAndamento, metrics.obrasAndamentoAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.obrasAndamento, metrics.obrasAndamentoAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Lucro Líquido"
            value={formatCurrency(metrics.lucroLiquidoTotal)}
            icon={TrendingUp}
            color="#84CC16"
            description={`${calculatePercentageChange(metrics.lucroLiquidoTotal, metrics.lucroLiquidoTotalAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.lucroLiquidoTotal, metrics.lucroLiquidoTotalAnterior).toFixed(1)}% vs mês anterior`}
          />
        </div>

        {/* 🔥 CONTROLES DE FLUXO - Orçamentos Fechados Disponíveis */}
        {orcamentosFechados.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Orçamentos Fechados Disponíveis ({orcamentosFechados.filter(o => !hasObraCreated(o._id)).length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orcamentosFechados
                .filter(orcamento => !hasObraCreated(orcamento._id)) // 🔥 CONTROLE DE FLUXO: Esconder se já tem obra
                .slice(0, 6)
                .map((orcamento) => (
                <div key={orcamento._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900 text-sm">{orcamento.nomeProjeto}</h3>
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      FECHADO
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{orcamento.nomeCliente}</p>
                  <p className="text-lg font-bold text-blue-600 mb-3">
                    {formatCurrency(orcamento.valorTotal)}
                  </p>
                  <button
                    onClick={() => handleCreateFromOrcamento(orcamento._id)}
                    className="w-full bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    Criar Obra
                  </button>
                </div>
              ))}
            </div>
            {orcamentosFechados.filter(o => !hasObraCreated(o._id)).length > 6 && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">
                  E mais {orcamentosFechados.filter(o => !hasObraCreated(o._id)).length - 6} orçamentos fechados disponíveis
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('obras')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'obras'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Lista de Obras ({obras.length})
              </button>
              <button
                onClick={() => setActiveTab('planejamento')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'planejamento'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                disabled={!selectedObra}
              >
                Planejamento da Obra {selectedObra ? `- ${selectedObra.nomeObra}` : ''}
              </button>
              <button
                onClick={() => setActiveTab('semanal')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'semanal'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                disabled={!selectedObra}
              >
                Planejamento Semanal {selectedObra ? `- ${selectedObra.nomeObra}` : ''}
              </button>
            </nav>
          </div>

          {/* 🔥 LISTA DE OBRAS EXPANDIDA */}
          {activeTab === 'obras' && (
            <>
              {/* Filtros e Busca */}
              <div className="p-6 border-b">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Buscar por nome da obra..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">Todos os Status</option>
                      <option value="planejamento">Planejamento</option>
                      <option value="em_andamento">Em Andamento</option>
                      <option value="pausada">Pausada</option>
                      <option value="concluida">Concluída</option>
                      <option value="cancelada">Cancelada</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Lista de Obras Expandida */}
              <div className="overflow-x-auto">
                {filteredObras.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma obra encontrada</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'Tente ajustar os filtros de busca'
                        : 'Comece criando uma nova obra'
                      }
                    </p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nome da Obra
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Etapa Atual
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Progresso
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Datas Previstas
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Datas Reais
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Desvio (dias)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredObras.map((obra) => {
                        const desvioInicio = obra.dataInicioReal && obra.dataInicioRealReal 
                          ? calculateDaysDifference(obra.dataInicioReal, obra.dataInicioRealReal)
                          : 0
                        const desvioTermino = obra.dataTerminoPrevisto && obra.dataTerminoReal
                          ? calculateDaysDifference(obra.dataTerminoPrevisto, obra.dataTerminoReal)
                          : 0
                        
                        return (
                          <tr key={obra._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{obra.nomeObra}</div>
                                <div className="text-sm text-gray-500">{obra.numeroContrato}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{getCurrentEtapa(obra)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full" 
                                    style={{ width: `${obra.progressoGeral}%` }}
                                  />
                                </div>
                                <span className="text-sm text-gray-600">{obra.progressoGeral.toFixed(1)}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(obra.status)}`}>
                                {obra.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>Início: {formatDate(obra.dataInicioReal)}</div>
                              <div>Término: {formatDate(obra.dataTerminoPrevisto)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>Início: {formatDate(obra.dataInicioRealReal)}</div>
                              <div>Término: {formatDate(obra.dataTerminoReal)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <div className={desvioInicio > 0 ? 'text-red-600' : desvioInicio < 0 ? 'text-green-600' : 'text-gray-600'}>
                                Início: {desvioInicio > 0 ? '+' : ''}{desvioInicio}
                              </div>
                              <div className={desvioTermino > 0 ? 'text-red-600' : desvioTermino < 0 ? 'text-green-600' : 'text-gray-600'}>
                                Término: {desvioTermino > 0 ? '+' : ''}{desvioTermino}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleViewObra(obra)}
                                  className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                  title="Planejamento da Obra"
                                >
                                  <Settings size={16} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedObra(obra)
                                    setActiveTab('semanal')
                                  }}
                                  className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                  title="Planejamento Semanal"
                                >
                                  <Calendar size={16} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedObra(obra)
                                    setShowGantt(true)
                                  }}
                                  className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50"
                                  title="Cronograma Gantt"
                                >
                                  <BarChart3 size={16} />
                                </button>
                                {obra.status === 'planejamento' && (
                                  <button
                                    onClick={() => handleIniciarObra(obra)}
                                    className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                    title="Dar Início"
                                  >
                                    <Play size={16} />
                                  </button>
                                )}
                                {obra.status === 'em_andamento' && (
                                  <button
                                    onClick={() => handleConcluirObra(obra)}
                                    className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                    title="Dar Concluído"
                                  >
                                    <CheckCircle size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={() => setEditingObra(obra)}
                                  className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
                                  title="Editar"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDelete(obra._id, 'obra')}
                                  className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                  title="Excluir"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* 🔥 PLANEJAMENTO DA OBRA EXPANDIDO */}
          {activeTab === 'planejamento' && selectedObra && (
            <div className="p-6">
              <div className="mb-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedObra.nomeObra}</h2>
                    <p className="text-gray-600">{selectedObra.descricaoProjeto}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span>Contrato: <strong>{selectedObra.numeroContrato}</strong></span>
                      <span>Responsável: <strong>{selectedObra.responsavelNome}</strong></span>
                      <span>Área: <strong>{selectedObra.areaObra || 'N/A'} m²</strong></span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowFuncionariosModal(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Users size={16} />
                      Funcionários
                    </button>
                    <button
                      onClick={() => setShowMaoObraModal(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <UserCheck size={16} />
                      Mão de Obra
                    </button>
                  </div>
                </div>
              </div>

              {/* Resumo Financeiro Expandido */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800">Custo Total</h3>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(selectedObra.valorOrcado)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-green-800">Valor Realizado</h3>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(selectedObra.valorRealizado)}
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-yellow-800">Custos Administrativos</h3>
                  <p className="text-2xl font-bold text-yellow-900">
                    {formatCurrency(selectedObra.custosAdministrativos)}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-purple-800">% Faturamento</h3>
                  <p className="text-2xl font-bold text-purple-900">
                    {selectedObra.percentualFaturamento.toFixed(2)}%
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-orange-800">Progresso Geral</h3>
                  <p className="text-2xl font-bold text-orange-900">
                    {selectedObra.progressoGeral.toFixed(1)}%
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${calculateLucroLiquido(selectedObra) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <h3 className={`text-sm font-medium ${calculateLucroLiquido(selectedObra) >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                    Lucro Líquido
                  </h3>
                  <p className={`text-2xl font-bold ${calculateLucroLiquido(selectedObra) >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                    {formatCurrency(calculateLucroLiquido(selectedObra))}
                  </p>
                </div>
              </div>

              {/* 🔥 GESTÃO DE ETAPAS EXPANDIDA */}
              <div className="bg-white border rounded-lg">
                <div className="p-4 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Gestão de Etapas</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowEtapaForm(true)}
                        className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
                      >
                        <Plus size={16} />
                        Incluir Etapa
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  {selectedObra.etapas && selectedObra.etapas.length > 0 ? (
                    <div className="space-y-4">
                      {selectedObra.etapas.map((etapa: any, index: number) => (
                        <div key={etapa.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-medium text-gray-900">Etapa {etapa.numero} - {etapa.nome}</h4>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(etapa.status)}`}>
                                  {etapa.status.replace('_', ' ').toUpperCase()}
                                </span>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => {
                                      setSelectedEtapaIndex(index)
                                      setShowSubetapaForm(true)
                                    }}
                                    className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                                  >
                                    + Subetapa
                                  </button>
                                  <button className="text-gray-500 hover:text-gray-700">
                                    <ArrowUp size={14} />
                                  </button>
                                  <button className="text-gray-500 hover:text-gray-700">
                                    <ArrowDown size={14} />
                                  </button>
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 mb-3">{etapa.descricao}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium">{etapa.percentualConcluido}% concluído</div>
                              <div className="text-xs text-gray-500">{formatCurrency(etapa.valorOrcado || 0)}</div>
                            </div>
                          </div>

                          {/* Informações da Etapa */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <div>
                              <span className="text-gray-500">Início Previsto:</span>
                              <div className="font-medium">{formatDate(etapa.dataInicioPrevisto)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Término Previsto:</span>
                              <div className="font-medium">{formatDate(etapa.dataTerminoPrevisto)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Início Real:</span>
                              <div className="font-medium">{formatDate(etapa.dataInicioReal)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Término Real:</span>
                              <div className="font-medium">{formatDate(etapa.dataTerminoReal)}</div>
                            </div>
                          </div>

                          {/* Dependências */}
                          {etapa.dependencias && etapa.dependencias.length > 0 && (
                            <div className="mb-4">
                              <span className="text-sm font-medium text-gray-700">Dependências:</span>
                              <div className="flex gap-2 mt-1">
                                {etapa.dependencias.map((depId: string) => {
                                  const depEtapa = selectedObra.etapas.find((e: any) => e.id === depId)
                                  return depEtapa ? (
                                    <span key={depId} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                                      Etapa {depEtapa.numero}
                                    </span>
                                  ) : null
                                })}
                              </div>
                            </div>
                          )}

                          {/* Insumos/Materiais */}
                          {etapa.insumos && etapa.insumos.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Package size={14} />
                                Insumos/Materiais ({etapa.insumos.length})
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {etapa.insumos.map((insumo: any) => (
                                  <div key={insumo.id} className="bg-gray-50 p-2 rounded text-sm">
                                    <div className="flex justify-between items-center">
                                      <span>{insumo.nome}</span>
                                      <div className="flex gap-2">
                                        <span>{insumo.quantidade} {insumo.unidade}</span>
                                        <button className="text-blue-600 hover:text-blue-800 text-xs">
                                          Delegar
                                        </button>
                                        <button className="text-green-600 hover:text-green-800 text-xs">
                                          Solicitar
                                        </button>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {formatCurrency(insumo.valorTotal)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Mão de Obra */}
                          {etapa.maoObra && etapa.maoObra.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Users size={14} />
                                Mão de Obra Alocada ({etapa.maoObra.length})
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {etapa.maoObra.map((mo: any) => (
                                  <div key={mo.id} className="bg-gray-50 p-2 rounded text-sm">
                                    <div className="flex justify-between items-center">
                                      <span>{mo.nome} - {mo.cargo}</span>
                                      <span>{mo.horas}h</span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {formatCurrency(mo.valorTotal)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Subetapas */}
                          {etapa.subetapas && etapa.subetapas.length > 0 && (
                            <div className="mt-4 ml-4 border-l-2 border-blue-200 pl-4">
                              <h5 className="font-medium text-sm mb-2">Subetapas:</h5>
                              {etapa.subetapas.map((subetapa: any) => (
                                <div key={subetapa.id} className="mb-3 p-3 bg-gray-50 rounded">
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-medium">Subetapa {subetapa.numero} - {subetapa.nome}</span>
                                    <span className="text-xs text-gray-500">{subetapa.percentualConcluido}% concluído</span>
                                  </div>
                                  
                                  {/* Insumos da Subetapa */}
                                  {subetapa.insumos && subetapa.insumos.length > 0 && (
                                    <div className="mb-2">
                                      <span className="text-xs font-medium text-gray-700">Insumos:</span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {subetapa.insumos.map((insumo: any) => (
                                          <span key={insumo.id} className="bg-white text-xs px-2 py-1 rounded border">
                                            {insumo.nome} ({insumo.quantidade})
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Mão de Obra da Subetapa */}
                                  {subetapa.maoObra && subetapa.maoObra.length > 0 && (
                                    <div>
                                      <span className="text-xs font-medium text-gray-700">Mão de Obra:</span>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {subetapa.maoObra.map((mo: any) => (
                                          <span key={mo.id} className="bg-white text-xs px-2 py-1 rounded border">
                                            {mo.nome} ({mo.horas}h)
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                      <p>Nenhuma etapa cadastrada</p>
                      <button
                        onClick={() => setShowEtapaForm(true)}
                        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                      >
                        Criar primeira etapa
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 🔥 PLANEJAMENTO SEMANAL EXPANDIDO */}
          {activeTab === 'semanal' && selectedObra && (
            <div className="p-6">
              <div className="mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Planejamento Semanal</h2>
                    <p className="text-gray-600">{selectedObra.nomeObra}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportPlanejamentoSemanalPDF}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2"
                    >
                      <Download size={16} />
                      Exportar PDF
                    </button>
                    <button
                      onClick={() => setShowChat(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                    >
                      <MessageSquare size={16} />
                      Chat
                    </button>
                    <button
                      onClick={() => setShowAtividadeForm(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Nova Atividade
                    </button>
                  </div>
                </div>
              </div>

              {/* Resumo Financeiro Semanal */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800">Valor Orçado</h3>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(selectedObra.valorOrcado)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-green-800">Valor Realizado</h3>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(selectedObra.valorRealizado)}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-purple-800">Progresso Geral</h3>
                  <p className="text-2xl font-bold text-purple-900">
                    {selectedObra.progressoGeral.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-yellow-800">Custos Administrativos</h3>
                  <p className="text-2xl font-bold text-yellow-900">
                    {formatCurrency(selectedObra.custosAdministrativos)}
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${calculateLucroLiquido(selectedObra) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <h3 className={`text-sm font-medium ${calculateLucroLiquido(selectedObra) >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                    Lucro Líquido
                  </h3>
                  <p className={`text-2xl font-bold ${calculateLucroLiquido(selectedObra) >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                    {formatCurrency(calculateLucroLiquido(selectedObra))}
                  </p>
                </div>
              </div>

              {/* Filtros para Atividades */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      placeholder="Buscar tarefas, etapas ou funcionário..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">Todos os Status</option>
                    <option value="pendente">Pendente</option>
                    <option value="em_andamento">Em Andamento</option>
                    <option value="concluida">Concluída</option>
                    <option value="atrasada">Atrasada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                  <select
                    value={funcionarioFilter}
                    onChange={(e) => setFuncionarioFilter(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">Todos os Funcionários</option>
                    {funcionarios.map(funcionario => (
                      <option key={funcionario._id} value={funcionario._id}>
                        {funcionario.nome}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <Filter size={16} className="text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {filteredAtividades.filter(a => a.obraId === selectedObra._id).length} atividades
                    </span>
                  </div>
                </div>
              </div>

              {/* Lista de Atividades Expandida */}
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tarefa
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Etapa/Subetapa
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Responsável
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Progresso
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Prazo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAtividades
                        .filter(atividade => atividade.obraId === selectedObra._id)
                        .map((atividade) => {
                          // Encontrar etapa/subetapa vinculada
                          const etapaVinculada = selectedObra.etapas?.find((e: any) => e.id === atividade.etapaId)
                          const subetapaVinculada = etapaVinculada?.subetapas?.find((s: any) => s.id === atividade.subetapaId)
                          
                          return (
                            <tr key={atividade._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{atividade.descricao}</div>
                                  {atividade.observacoes && (
                                    <div className="text-sm text-gray-500">{atividade.observacoes}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {etapaVinculada ? (
                                  <div>
                                    <div>Etapa {etapaVinculada.numero}</div>
                                    {subetapaVinculada && (
                                      <div className="text-xs text-gray-500">Sub {subetapaVinculada.numero}</div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">Não vinculada</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {atividade.funcionarioResponsavel}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(atividade.status)}`}>
                                  {atividade.status.replace('_', ' ').toUpperCase()}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full" 
                                      style={{ width: `${atividade.progresso}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-gray-600">{atividade.progresso}%</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(atividade.prazo)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => {
                                      setSelectedAtividade(atividade)
                                      const funcionario = funcionarios.find(f => f._id === atividade.funcionarioId)
                                      setSelectedFuncionario(funcionario)
                                      setShowChat(true)
                                    }}
                                    className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                    title="Chat da Tarefa"
                                  >
                                    <MessageSquare size={16} />
                                  </button>
                                  <button
                                    onClick={() => setEditingAtividade(atividade)}
                                    className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
                                    title="Editar"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(atividade._id, 'atividade')}
                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                    title="Excluir"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                </div>

                {filteredAtividades.filter(a => a.obraId === selectedObra._id).length === 0 && (
                  <div className="text-center py-12">
                    <Clock className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma atividade encontrada</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Comece criando uma nova atividade
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <PlanejamentoObraForm
        isOpen={showObraForm}
        onClose={() => setShowObraForm(false)}
        onSubmit={createObra}
        title="Criar Nova Obra"
        contratosAssinados={contratosAssinados}
        orcamentosFechados={orcamentosFechados}
        funcionarios={funcionarios}
        insumos={insumos}
      />

      <PlanejamentoObraForm
        isOpen={!!editingObra}
        onClose={() => setEditingObra(null)}
        onSubmit={(data) => updateObra(editingObra._id, data)}
        initialData={editingObra}
        title="Editar Obra"
        contratosAssinados={contratosAssinados}
        orcamentosFechados={orcamentosFechados}
        funcionarios={funcionarios}
        insumos={insumos}
      />

      <AtividadeForm
        isOpen={showAtividadeForm}
        onClose={() => setShowAtividadeForm(false)}
        onSubmit={createAtividade}
        title="Nova Atividade"
        obraId={selectedObra?._id || ''}
        funcionarios={funcionarios}
        insumos={insumos}
        etapas={selectedObra?.etapas || []}
      />

      <AtividadeForm
        isOpen={!!editingAtividade}
        onClose={() => setEditingAtividade(null)}
        onSubmit={(data) => updateAtividade(editingAtividade._id, data)}
        initialData={editingAtividade}
        title="Editar Atividade"
        obraId={selectedObra?._id || ''}
        funcionarios={funcionarios}
        insumos={insumos}
        etapas={selectedObra?.etapas || []}
      />

      {/* 🔥 MODAL PARA INCLUIR ETAPA */}
      {showEtapaForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Incluir Nova Etapa</h3>
              <button
                onClick={() => setShowEtapaForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Etapa *
                </label>
                <input
                  type="text"
                  value={newEtapa.nome}
                  onChange={(e) => setNewEtapa(prev => ({ ...prev, nome: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Fundação, Estrutura, Acabamento..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={newEtapa.descricao}
                  onChange={(e) => setNewEtapa(prev => ({ ...prev, descricao: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Descrição detalhada da etapa..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Início Previsto
                  </label>
                  <input
                    type="date"
                    value={newEtapa.dataInicioPrevisto}
                    onChange={(e) => setNewEtapa(prev => ({ ...prev, dataInicioPrevisto: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Término Previsto
                  </label>
                  <input
                    type="date"
                    value={newEtapa.dataTerminoPrevisto}
                    onChange={(e) => setNewEtapa(prev => ({ ...prev, dataTerminoPrevisto: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Orçado (R$)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newEtapa.valorOrcado}
                  onChange={(e) => setNewEtapa(prev => ({ ...prev, valorOrcado: parseFloat(e.target.value) || 0 }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEtapaForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddEtapa}
                disabled={!newEtapa.nome.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Incluir Etapa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔥 MODAL PARA INCLUIR SUBETAPA */}
      {showSubetapaForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Incluir Nova Subetapa</h3>
              <button
                onClick={() => setShowSubetapaForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Subetapa *
                </label>
                <input
                  type="text"
                  value={newSubetapa.nome}
                  onChange={(e) => setNewSubetapa(prev => ({ ...prev, nome: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Escavação, Concretagem, Impermeabilização..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={newSubetapa.descricao}
                  onChange={(e) => setNewSubetapa(prev => ({ ...prev, descricao: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Descrição detalhada da subetapa..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Início Previsto
                  </label>
                  <input
                    type="date"
                    value={newSubetapa.dataInicioPrevisto}
                    onChange={(e) => setNewSubetapa(prev => ({ ...prev, dataInicioPrevisto: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Término Previsto
                  </label>
                  <input
                    type="date"
                    value={newSubetapa.dataTerminoPrevisto}
                    onChange={(e) => setNewSubetapa(prev => ({ ...prev, dataTerminoPrevisto: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Orçado (R$)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newSubetapa.valorOrcado}
                  onChange={(e) => setNewSubetapa(prev => ({ ...prev, valorOrcado: parseFloat(e.target.value) || 0 }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0,00"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSubetapaForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddSubetapa}
                disabled={!newSubetapa.nome.trim()}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Incluir Subetapa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gantt Chart Modal */}
      {showGantt && selectedObra && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-7xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Cronograma Gantt - {selectedObra.nomeObra}</h2>
                <button
                  onClick={() => setShowGantt(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <GanttChart
                etapas={selectedObra.etapas || []}
                dataInicio={selectedObra.dataInicioRealReal}
                dataFim={selectedObra.dataTerminoReal}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Funcionários Envolvidos */}
      {showFuncionariosModal && selectedObra && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Funcionários Envolvidos - {selectedObra.nomeObra}</h2>
                <button
                  onClick={() => setShowFuncionariosModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6">
              {getFuncionariosEnvolvidos(selectedObra).map(funcionario => (
                <div key={funcionario._id} className="border border-gray-200 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">{funcionario.nome}</h3>
                      <p className="text-sm text-gray-600">{funcionario.cargo}</p>
                      <p className="text-sm text-gray-500">{funcionario.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{formatCurrency(funcionario.valorRemuneracao || 0)}</p>
                      <p className="text-xs text-gray-500">/{funcionario.unidadeRemuneracao}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Mão de Obra Envolvida */}
      {showMaoObraModal && selectedObra && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Mão de Obra Envolvida - {selectedObra.nomeObra}</h2>
                <button
                  onClick={() => setShowMaoObraModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6">
              {getMaoObraEnvolvida(selectedObra).map((mo, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">{mo.nome}</h3>
                      <p className="text-sm text-gray-600">{mo.cargo}</p>
                      <p className="text-sm text-gray-500">{mo.etapa} ({mo.tipo})</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{mo.horas}h × {formatCurrency(mo.valorHora)}</p>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(mo.valorTotal)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal Expandido */}
      {showChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  Chat - {selectedObra?.nomeObra}
                  {selectedFuncionario && ` - ${selectedFuncionario.nome}`}
                  {selectedAtividade && ` - ${selectedAtividade.descricao}`}
                </h2>
                <button
                  onClick={() => {
                    setShowChat(false)
                    setSelectedFuncionario(null)
                    setSelectedAtividade(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Seletor de Funcionário */}
              {!selectedFuncionario && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selecionar Funcionário
                  </label>
                  <select
                    onChange={(e) => {
                      const funcionario = funcionarios.find(f => f._id === e.target.value)
                      setSelectedFuncionario(funcionario)
                    }}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Escolha um funcionário</option>
                    {funcionarios.map(funcionario => (
                      <option key={funcionario._id} value={funcionario._id}>
                        {funcionario.nome} - {funcionario.cargo}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Mensagens */}
              {selectedFuncionario && (
                <>
                  <div className="h-64 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4">
                    {getMensagensByObra(selectedObra?._id || '')
                      .filter(msg => {
                        const matchesFuncionario = msg.destinatarioId === selectedFuncionario._id || msg.remetenteId === selectedFuncionario._id
                        const matchesAtividade = !selectedAtividade || msg.atividadeId === selectedAtividade._id
                        return matchesFuncionario && matchesAtividade
                      })
                      .map(mensagem => (
                      <div key={mensagem._id} className={`mb-3 ${
                        mensagem.remetenteId === selectedFuncionario._id ? 'text-right' : 'text-left'
                      }`}>
                        <div className={`inline-block p-3 rounded-lg max-w-xs ${
                          mensagem.remetenteId === selectedFuncionario._id 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-200 text-gray-900'
                        }`}>
                          <p className="text-sm">{mensagem.mensagem}</p>
                          <p className="text-xs mt-1 opacity-75">
                            {new Date(mensagem.dataEnvio).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Input de Nova Mensagem */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Digite sua mensagem..."
                      className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <button
                      onClick={handleSendMessage}
                      className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700"
                    >
                      Enviar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PlanejamentoObras
